import { Component, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { OrgService, type Organization, type Membership, type MemberWithUser, type Invitation, type OrgRole } from '../../../core/services/org.service';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-team-settings',
  templateUrl: './team-settings.component.html',
})
export class TeamSettingsComponent implements OnInit {
  org: Organization | null = null;
  myMembership: Membership | null = null;
  members: MemberWithUser[] = [];
  invitations: Invitation[] = [];

  loading = true;
  error: string | null = null;

  // Org name edit
  editingName = false;
  nameInput = '';
  nameSaving = false;
  nameError: string | null = null;

  // Invite form
  inviteEmail = '';
  inviteRole: OrgRole = 'member';
  inviteLoading = false;
  inviteError: string | null = null;

  // Role change
  updatingMemberId: string | null = null;

  currentUserId = '';

  constructor(
    private readonly auth: AuthService,
    private readonly orgService: OrgService,
    private readonly toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.auth.currentUser$.pipe(take(1)).subscribe(user => {
      this.currentUserId = user?.userId ?? '';
      this.loadOrg();
    });
  }

  get isAdminOrOwner(): boolean {
    return this.myMembership?.role === 'owner' || this.myMembership?.role === 'admin';
  }

  get isOwner(): boolean {
    return this.myMembership?.role === 'owner';
  }

  get seatLimit(): number {
    return this.org?.teamMemberLimit ?? 1;
  }

  get seatUsed(): number {
    return this.members.filter(m => m.membershipStatus === 'active').length + this.invitations.length;
  }

  get canInvite(): boolean {
    return this.isAdminOrOwner && this.seatUsed < this.seatLimit;
  }

  private loadOrg(): void {
    this.loading = true;
    this.error = null;

    this.orgService.getMyOrgs().subscribe({
      next: res => {
        if (res.memberships.length === 0) {
          this.loading = false;
          return;
        }
        const { org, membership } = res.memberships[0];
        this.org = org;
        this.myMembership = membership;
        this.loadMembersAndInvitations(org.orgId);
      },
      error: () => {
        this.error = 'Failed to load organization data.';
        this.loading = false;
      },
    });
  }

  private loadMembersAndInvitations(orgId: string): void {
    this.orgService.listMembers(orgId).subscribe({
      next: res => {
        this.members = res.members;
        if (this.isAdminOrOwner) {
          this.orgService.listInvitations(orgId).subscribe({
            next: inv => {
              this.invitations = inv.invitations;
              this.loading = false;
            },
            error: () => { this.loading = false; },
          });
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.error = 'Failed to load team members.';
        this.loading = false;
      },
    });
  }

  sendInvite(): void {
    const email = this.inviteEmail.trim().toLowerCase();
    if (!email || !this.org) return;

    this.inviteLoading = true;
    this.inviteError = null;

    this.orgService.createInvitation(this.org.orgId, email, this.inviteRole).subscribe({
      next: res => {
        this.invitations = [res.invitation, ...this.invitations];
        this.inviteEmail = '';
        this.inviteRole = 'member';
        this.inviteLoading = false;
        this.toast.show('Invitation sent', 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.inviteError = err?.error?.message ?? 'Failed to send invitation.';
        this.inviteLoading = false;
      },
    });
  }

  revokeInvitation(inviteId: string): void {
    if (!this.org) return;
    this.orgService.revokeInvitation(this.org.orgId, inviteId).subscribe({
      next: () => {
        this.invitations = this.invitations.filter(i => i.inviteId !== inviteId);
        this.toast.show('Invitation revoked', 'success');
      },
      error: () => this.toast.show('Failed to revoke invitation', 'error'),
    });
  }

  changeRole(memberId: string, role: OrgRole): void {
    if (!this.org || this.updatingMemberId) return;
    this.updatingMemberId = memberId;

    this.orgService.updateMemberRole(this.org.orgId, memberId, role).subscribe({
      next: () => {
        this.members = this.members.map(m =>
          m.userId === memberId ? { ...m, role } : m,
        );
        this.updatingMemberId = null;
        this.toast.show('Role updated', 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.toast.show(err?.error?.message ?? 'Failed to update role', 'error');
        this.updatingMemberId = null;
      },
    });
  }

  removeMember(memberId: string): void {
    if (!this.org) return;
    this.orgService.removeMember(this.org.orgId, memberId).subscribe({
      next: () => {
        this.members = this.members.filter(m => m.userId !== memberId);
        this.toast.show('Member removed', 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.toast.show(err?.error?.message ?? 'Failed to remove member', 'error');
      },
    });
  }

  startEditingName(): void {
    this.nameInput = this.org?.name ?? '';
    this.nameError = null;
    this.editingName = true;
  }

  cancelEditingName(): void {
    this.editingName = false;
    this.nameError = null;
  }

  saveName(): void {
    const trimmed = this.nameInput.trim();
    if (!trimmed || !this.org || trimmed === this.org.name) {
      this.editingName = false;
      return;
    }

    this.nameSaving = true;
    this.nameError = null;

    this.orgService.updateOrgName(this.org.orgId, trimmed).subscribe({
      next: res => {
        this.org = res.org;
        this.editingName = false;
        this.nameSaving = false;
        this.toast.show('Organization name updated', 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.nameError = err?.error?.message ?? 'Failed to update name.';
        this.nameSaving = false;
      },
    });
  }

  trackByUserId(_: number, member: MemberWithUser): string {
    return member.userId;
  }

  trackByInviteId(_: number, invite: Invitation): string {
    return invite.inviteId;
  }
}
